import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getDivisions } from '../utils/divisions';
import { Wrench, Truck, ShieldCheck, Lock, LogOut, Sparkles, UserPlus, CreditCard, Settings as SettingsIcon } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function Home() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <div className="text-slate-400">Loading...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;

  const divisions = getDivisions(user);
  const showShop = divisions.includes('shop');
  const showField = divisions.includes('field');
  const showFieldLocked = !showField && user.field_locked;
  const showAdmin = divisions.includes('admin');
  const tileCount = [showShop, showField || showFieldLocked, showAdmin].filter(Boolean).length;
  const isManagement = user.role === 'management';
  const perms = user.effective_permissions || [];
  const orgLinks = [
    { path: '/users', icon: UserPlus, label: 'Users', perm: 'users', testid: 'home-users-tile' },
    { path: '/billing', icon: CreditCard, label: 'Billing', perm: 'billing', testid: 'home-billing-tile' },
    { path: '/settings', icon: SettingsIcon, label: 'Settings', perm: 'settings', testid: 'home-settings-tile' },
  ].filter((l) => perms.includes(l.perm));

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col" data-testid="home-dashboard">
      <header className="flex items-center justify-between px-8 py-6">
        <div>
          <h1 className="text-2xl font-black tracking-tight text-white">
            ShopManager<span className="text-orange-400">247</span>
          </h1>
          <p className="text-xs text-slate-500 uppercase tracking-wider mt-0.5">Operations Hub</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right hidden sm:block">
            <p className="text-sm font-semibold text-white">{user.name}</p>
            <p className="text-xs text-slate-500 uppercase tracking-wider">{user.role?.replace(/_/g, ' ')}</p>
          </div>
          <Button
            variant="ghost"
            onClick={handleLogout}
            data-testid="home-logout-button"
            className="text-slate-400 hover:text-white hover:bg-slate-800"
          >
            <LogOut size={18} className="mr-2" /> Logout
          </Button>
        </div>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center px-6 pb-24">
        <h2 className="text-base md:text-lg font-semibold text-slate-400 uppercase tracking-widest mb-10">
          Choose your division
        </h2>
        <div className={`grid grid-cols-1 gap-8 w-full ${tileCount >= 3 ? 'md:grid-cols-3 max-w-5xl' : 'md:grid-cols-2 max-w-3xl'}`}>
          {showShop && (
            <Link
              to="/dashboard"
              data-testid="home-shop-card"
              className="group rounded-2xl border-2 border-slate-800 bg-slate-900 p-10 flex flex-col items-center text-center transition-all duration-200 hover:border-orange-500 hover:-translate-y-1"
            >
              <div className="w-20 h-20 rounded-2xl bg-orange-500/10 border border-orange-500/30 flex items-center justify-center mb-6 transition-transform duration-200 group-hover:scale-110">
                <Wrench size={40} className="text-orange-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Shop</h3>
              <p className="text-sm text-slate-400">Jobs, scheduling, equipment &amp; shop reporting</p>
            </Link>
          )}

          {showField && (
            <Link
              to="/field/calendar"
              data-testid="home-field-card"
              className="group rounded-2xl border-2 border-slate-800 bg-slate-900 p-10 flex flex-col items-center text-center transition-all duration-200 hover:border-sky-500 hover:-translate-y-1"
            >
              <div className="w-20 h-20 rounded-2xl bg-sky-500/10 border border-sky-500/30 flex items-center justify-center mb-6 transition-transform duration-200 group-hover:scale-110">
                <Truck size={40} className="text-sky-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Field</h3>
              <p className="text-sm text-slate-400">Dispatch calendar, field scheduling &amp; assignments</p>
            </Link>
          )}

          {showFieldLocked && (
            <div
              data-testid="home-field-locked-card"
              onClick={() => isManagement && navigate('/billing')}
              className={`rounded-2xl border-2 border-dashed border-slate-800 bg-slate-900/50 p-10 flex flex-col items-center text-center ${isManagement ? 'cursor-pointer hover:border-amber-500/60' : ''} transition-colors`}
            >
              <div className="w-20 h-20 rounded-2xl bg-slate-800/60 border border-slate-700 flex items-center justify-center mb-6">
                <Lock size={36} className="text-slate-500" />
              </div>
              <h3 className="text-2xl font-bold text-slate-400 mb-2 flex items-center gap-2">
                Field
                <span className="text-[10px] px-2 py-0.5 bg-amber-500/15 text-amber-400 rounded-full font-semibold uppercase tracking-wider flex items-center gap-1">
                  <Sparkles size={10} /> Silver
                </span>
              </h3>
              <p className="text-sm text-slate-500">
                {isManagement
                  ? 'The Field division requires the Silver tier. Click to upgrade your plan.'
                  : 'The Field division requires the Silver tier. Contact your administrator.'}
              </p>
            </div>
          )}

          {showAdmin && (
            <Link
              to={(user?.effective_permissions || []).includes('safety-docs') ? '/admin/safety-docs' : '/admin/compliance'}
              data-testid="home-admin-card"
              className="group rounded-2xl border-2 border-slate-800 bg-slate-900 p-10 flex flex-col items-center text-center transition-all duration-200 hover:border-emerald-500 hover:-translate-y-1"
            >
              <div className="w-20 h-20 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mb-6 transition-transform duration-200 group-hover:scale-110">
                <ShieldCheck size={40} className="text-emerald-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Admin</h3>
              <p className="text-sm text-slate-400">Forms, safety docs, compliance, equipment &amp; customers</p>
            </Link>
          )}

        </div>

        {orgLinks.length > 0 && (
          <div className="w-full max-w-3xl mt-12">
            <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-widest mb-4 text-center">Organization</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="home-org-links">
              {orgLinks.map((l) => {
                const Icon = l.icon;
                return (
                  <Link
                    key={l.path}
                    to={l.path}
                    data-testid={l.testid}
                    className="group rounded-xl border border-slate-800 bg-slate-900/70 px-5 py-4 flex items-center gap-3 transition-all duration-200 hover:border-slate-600 hover:-translate-y-0.5"
                  >
                    <span className="w-9 h-9 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center transition-colors group-hover:border-slate-500">
                      <Icon size={18} className="text-slate-300" />
                    </span>
                    <span className="text-sm font-semibold text-slate-200">{l.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
